import { Period as PeriodResult, PeriodVehicle as PeriodVehicleResult } from "@shared/platform-api/generated"
import { localDateTimeFromString } from "@shared/utils/formatters/dates"
import { kmToMileConverterWithDecimals } from "@shared/utils/formatters/kmToMiles"
import { getSpeedBands, getSpeedingPercentage } from "../utils"
import { InsightsPeriod, InsightsPeriodNumber, PeriodVehicle } from "./types"

export type GetPeriodsOutput = {
  periods: ReadonlyArray<InsightsPeriod>
  currentPeriodIndex: number
}

const getCurrentPeriodIndex = (result: ReadonlyArray<PeriodResult>): number => {
  const i = result.findIndex((period) => {
    const today = new Date().toISOString()
    // If period end time is greater than today's date, we are in that period
    return period.endedAt > today
  })
  // If today is greater than all period end times, the policy has finished and we should select the last period
  return i !== -1 ? i : result.length - 1
}

export const fromPeriodApi = (result: Array<PeriodResult>): GetPeriodsOutput => ({
  periods: result.map((it) => {
    const speedBands = it.analytics
      ? getSpeedBands(
          it.analytics.minorSpeedBandPercentage,
          it.analytics.majorSpeedBandPercentage,
          it.analytics.criticalSpeedBandPercentage
        )
      : null
    return {
      number: it.number as InsightsPeriodNumber,
      startedAt: localDateTimeFromString(it.startedAt),
      endedAt: localDateTimeFromString(it.endedAt),
      analytics: it.analytics
        ? {
            score: it.analytics.safetyScore ?? null,
            speedBands,
            distance: kmToMileConverterWithDecimals(it.analytics.distanceKm),
            distanceSpentSpeeding: getSpeedingPercentage(speedBands),
            averageSpeedExceedencePercentage: it.analytics.averageSpeedExceedencePercentage || null,
          }
        : null,
    }
  }),
  currentPeriodIndex: getCurrentPeriodIndex(result),
})

export const fromPeriodVehicleApi = (result: Array<PeriodVehicleResult>): ReadonlyArray<PeriodVehicle> =>
  result.map((it) => {
    const speedBands = getSpeedBands(
      it.minorSpeedBandPercentage,
      it.majorSpeedBandPercentage,
      it.criticalSpeedBandPercentage
    )
    return {
      vrn: it.vrn,
      name: it.name,
      score: it.safetyScore,
      speedBands,
      distanceSpentSpeeding: getSpeedingPercentage(speedBands),
      averageSpeedExceedencePercentage: it.averageSpeedExceedencePercentage || null,
    }
  })
